import {
  FiActivity,
  FiBarChart2,
  FiCalendar,
  FiClipboard,
  FiCode,
  FiCompass,
  FiCpu,
  FiCreditCard,
  FiEye,
  FiFileText,
  FiHeart,
  FiInbox,
  FiKey,
  FiLayers,
  FiList,
  FiLock,
  FiMessageSquare,
  FiSearch,
  FiServer,
  FiSettings,
  FiShare2,
  FiShield,
  FiSmartphone,
  FiTrendingUp,
  FiUpload,
  FiUser,
  FiUserPlus,
  FiUsers,
  FiVideo,
  FiZap,
} from 'react-icons/fi';
import type { GuideGroup, GuideLocation, GuideMeta, RoleKey } from './types';

export const GUIDE_GROUPS: GuideGroup[] = [
  'gettingStarted',
  'patients',
  'exams',
  'review',
  'scheduling',
  'analytics',
  'organization',
  'developers',
  'other',
];

export const DEFAULT_GUIDE_ID = 'getting-started';

export const ALL_ROLES: GuideMeta['audience'] = [
  'midwife',
  'sonographer',
  'gp',
  'ob',
  'mfm_specialist',
  'receptionist',
  'facility_admin',
  'it',
  'division_lead',
  'provincial_coordinator',
  'regional_coordinator',
  'org_admin',
  'compliance_officer',
  'radiology_consultant',
  'external_reviewer',
];

const orgTab =
  (...tabs: string[]) =>
  ({ pathname, search }: GuideLocation) =>
    pathname.toLowerCase() === '/organization' &&
    tabs.includes(new URLSearchParams(search).get('pacsTab') ?? '');

const viewerParam =
  (name: string, ...values: string[]) =>
  ({ pathname, search }: GuideLocation) =>
    pathname.toLowerCase() === '/exam/viewer' &&
    values.includes(new URLSearchParams(search).get(name) ?? '');

const CLINICAL: RoleKey[] = [
  'midwife',
  'sonographer',
  'gp',
  'ob',
  'mfm_specialist',
];
const ADMINS: RoleKey[] = ['org_admin', 'facility_admin'];

export const GUIDES: GuideMeta[] = [
  {
    id: 'getting-started',
    group: 'gettingStarted',
    icon: FiCompass,
    routes: [],
    audience: ALL_ROLES,
    related: ['managing-patients'],
    keywords: [
      'navigation',
      'sidebar',
      'notifications',
      'facility',
      'scope',
      'welcome',
      'report a problem',
    ],
  },
  {
    id: 'roles-and-permissions',
    group: 'gettingStarted',
    icon: FiShield,
    audience: ALL_ROLES,
    related: ['getting-started', 'roles-editor'],
    keywords: ['roles', 'permissions', 'access', 'scope', 'badges'],
  },
  {
    id: 'signing-in',
    group: 'gettingStarted',
    icon: FiLock,
    audience: ALL_ROLES,
    related: ['two-factor-authentication', 'account-and-preferences'],
    keywords: [
      'login',
      'password',
      'sso',
      'google',
      'invite',
      'account',
      'signup',
      'training',
      'phone',
    ],
  },
  {
    id: 'two-factor-authentication',
    group: 'gettingStarted',
    icon: FiKey,
    routes: ['/2fa-setup'],
    audience: ALL_ROLES,
    related: ['signing-in', 'security-and-sso'],
    keywords: ['2fa', 'authenticator', 'otp', 'security', 'verify', 'tablet'],
  },
  {
    id: 'account-and-preferences',
    group: 'gettingStarted',
    icon: FiUser,
    routes: ['/profile'],
    audience: ALL_ROLES,
    related: ['two-factor-authentication', 'getting-started'],
    keywords: [
      'profile',
      'password',
      'language',
      'theme',
      'appointment',
      'ownership',
    ],
  },
  {
    id: 'training-subscription',
    group: 'gettingStarted',
    icon: FiCreditCard,
    routes: ['/subscription'],
    modes: ['full'],
    audience: ['gp'],
    related: ['signing-in', 'mobile-licensing', 'two-factor-authentication'],
    keywords: [
      'subscription',
      'training',
      'plan',
      'devices',
      'license',
      'tablet',
      'stripe',
      'trial',
    ],
  },
  {
    id: 'managing-patients',
    group: 'patients',
    icon: FiUsers,
    routes: ['/patients', '/patients/new'],
    match: ({ pathname }) => /^\/patients\/[^/]+\/edit\/?$/i.test(pathname),
    permissions: ['patient.read'],
    modes: ['full'],
    audience: [
      'midwife',
      'sonographer',
      'gp',
      'ob',
      'mfm_specialist',
      'receptionist',
      'facility_admin',
    ],
    related: ['getting-started'],
    keywords: [
      'archive',
      'search',
      'create',
      'edit',
      'delete',
      'dossier',
      'past pregnancies',
      'obstetric history',
    ],
  },
  {
    id: 'patient-timeline-and-pregnancy',
    group: 'patients',
    icon: FiHeart,
    match: ({ pathname }) => /^\/patients\/(?!new$)[^/]+\/?$/i.test(pathname),
    permissions: ['patient.read'],
    modes: ['full'],
    audience: [...CLINICAL, 'receptionist', 'facility_admin'],
    related: ['managing-patients', 'exam-viewer', 'writing-the-report'],
    keywords: [
      'pregnancy',
      'timeline',
      'delivery',
      'family history',
      'close pregnancy',
      'dating',
      'past pregnancies',
      'fetus',
    ],
  },
  {
    id: 'new-case-upload',
    group: 'exams',
    icon: FiUpload,
    routes: ['/new-case'],
    permissions: ['study.acquire'],
    modes: ['full'],
    audience: CLINICAL,
    related: ['managing-patients', 'exam-management', 'blind-sweep'],
    keywords: ['upload', 'dicom', 'video', 'case', 'analysis'],
  },
  {
    id: 'pacs-inbox',
    group: 'exams',
    icon: FiInbox,
    routes: ['/pacs-inbox'],
    permissions: ['pacs_device.read'],
    modes: ['full'],
    audience: [...CLINICAL, 'it', 'facility_admin'],
    related: ['pacs-connectivity', 'exam-management'],
    keywords: ['pacs', 'inbox', 'studies', 'ultrasound', 'attach'],
  },
  {
    id: 'exam-management',
    group: 'exams',
    icon: FiList,
    routes: ['/exams'],
    permissions: ['study.read'],
    modes: ['full'],
    audience: [...CLINICAL, 'facility_admin'],
    related: ['exam-viewer', 'new-case-upload', 'pacs-inbox'],
    keywords: ['exams', 'filter', 'export', 'archive', 'search'],
  },
  {
    id: 'realtime-exam',
    group: 'exams',
    icon: FiVideo,
    routes: ['/realtimeexam', '/realtime'],
    permissions: ['study.acquire'],
    modes: ['full'],
    audience: CLINICAL,
    related: ['blind-sweep', 'understanding-ai-results', 'writing-the-report'],
    keywords: ['realtime', 'video feed', 'calibration', 'live exam', 'ai'],
  },
  {
    id: 'blind-sweep',
    group: 'exams',
    icon: FiActivity,
    routes: ['/sweep'],
    permissions: ['study.read'],
    modes: ['full'],
    audience: CLINICAL,
    related: ['realtime-exam', 'new-case-upload', 'understanding-ai-results'],
    keywords: ['sweep', 'protocol', 'viewer', 'report'],
  },
  {
    id: 'exam-viewer',
    group: 'review',
    icon: FiEye,
    routes: ['/exam/viewer'],
    permissions: ['study.read'],
    modes: ['full'],
    audience: [
      ...CLINICAL,
      'facility_admin',
      'radiology_consultant',
      'external_reviewer',
    ],
    related: [
      'understanding-ai-results',
      'writing-the-report',
      'clinical-collaboration',
    ],
    keywords: ['viewer', 'images', 'medical views', 'panels'],
  },
  {
    id: 'understanding-ai-results',
    group: 'review',
    icon: FiCpu,
    match: (loc) =>
      !viewerParam('tab', 'report')(loc) &&
      (viewerParam('panel', 'findings', 'analysis')(loc) ||
        viewerParam('tab', 'findings')(loc)),
    permissions: ['study.read'],
    modes: ['full'],
    audience: [...CLINICAL, 'radiology_consultant', 'external_reviewer'],
    related: ['exam-viewer', 'writing-the-report'],
    keywords: ['ai', 'findings', 'quality', 'measurements', 'gestational age'],
  },
  {
    id: 'writing-the-report',
    group: 'review',
    icon: FiFileText,
    match: viewerParam('tab', 'report'),
    permissions: ['report.read'],
    modes: ['full'],
    audience: [...CLINICAL, 'radiology_consultant'],
    related: [
      'exam-viewer',
      'sharing-with-patients',
      'understanding-ai-results',
    ],
    keywords: ['report', 'trimester', 'autosave', 'print', 'export'],
  },
  {
    id: 'sharing-with-patients',
    group: 'review',
    icon: FiShare2,
    permissions: ['report.read'],
    modes: ['full'],
    audience: CLINICAL,
    related: ['writing-the-report'],
    keywords: ['share', 'secure link', 'revoke', 'patient'],
  },
  {
    id: 'clinical-collaboration',
    group: 'review',
    icon: FiMessageSquare,
    match: viewerParam('panel', 'collaboration'),
    permissions: ['study.read'],
    modes: ['full'],
    audience: [...CLINICAL, 'radiology_consultant', 'external_reviewer'],
    related: ['exam-viewer', 'roles-and-permissions'],
    keywords: ['collaboration', 'discussion', 'review', 'second opinion'],
  },
  {
    id: 'scheduling',
    group: 'scheduling',
    icon: FiCalendar,
    routes: ['/Scheduling'],
    permissions: ['appointment.read'],
    modes: ['full'],
    audience: [...CLINICAL, 'receptionist', 'facility_admin'],
    related: ['managing-patients', 'new-case-upload'],
    keywords: ['appointment', 'worklist', 'booking', 'schedule'],
  },
  {
    id: 'dashboard',
    group: 'analytics',
    icon: FiBarChart2,
    routes: ['/home'],
    modes: ['full'],
    audience: ALL_ROLES,
    related: ['getting-started', 'prediction-performance-and-exam-analytics'],
    keywords: ['dashboard', 'analytics', 'quick actions', 'key figures'],
  },
  {
    id: 'audit-trail',
    group: 'analytics',
    icon: FiSearch,
    routes: ['/audit-trail'],
    permissions: ['audit_log.read'],
    audience: [
      ...ADMINS,
      'compliance_officer',
      'division_lead',
      'regional_coordinator',
      'provincial_coordinator',
    ],
    related: ['roles-and-permissions'],
    keywords: ['audit', 'log', 'export', 'search'],
  },
  {
    id: 'prediction-performance-and-exam-analytics',
    group: 'analytics',
    icon: FiTrendingUp,
    routes: ['/prediction-performance', '/exam-analytics'],
    permissions: ['audit_log.read'],
    modes: ['full'],
    audience: [...ADMINS, 'compliance_officer'],
    related: ['dashboard', 'understanding-ai-results'],
    keywords: ['ai performance', 'analytics', 'blind sweep', 'monitoring'],
  },
  {
    id: 'organization-profile-and-structure',
    group: 'organization',
    icon: FiLayers,
    routes: ['/organization', '/org-structure'],
    match: orgTab('clinical', 'org-structure'),
    permissions: ['org_unit.read'],
    modes: ['full'],
    audience: [
      ...ADMINS,
      'division_lead',
      'regional_coordinator',
      'provincial_coordinator',
    ],
    related: ['medical-staff', 'roles-editor'],
    keywords: ['organization', 'facility', 'structure', 'profile', 'tree'],
  },
  {
    id: 'medical-staff',
    group: 'organization',
    icon: FiUserPlus,
    match: orgTab('staff'),
    permissions: ['user.read'],
    modes: ['full'],
    audience: [...ADMINS, 'division_lead'],
    related: ['roles-and-permissions', 'roles-editor'],
    keywords: ['staff', 'users', 'csv import', 'credentials', 'roles'],
  },
  {
    id: 'roles-editor',
    group: 'organization',
    icon: FiKey,
    routes: ['/roles-permissions'],
    match: orgTab('roles-permissions'),
    permissions: ['role.read'],
    modes: ['full'],
    audience: [...ADMINS, 'division_lead'],
    related: ['roles-and-permissions', 'medical-staff'],
    keywords: ['roles', 'permissions', 'diff', 'editor'],
  },
  {
    id: 'pacs-connectivity',
    group: 'organization',
    icon: FiServer,
    match: orgTab('pacs', 'servers'),
    permissions: ['pacs_device.manage'],
    modes: ['full'],
    audience: ['it', ...ADMINS],
    related: ['pacs-inbox', 'mobile-licensing'],
    keywords: [
      'pacs',
      'dicom',
      'application entities',
      'connectivity',
      'ultrasound machine',
    ],
  },
  {
    id: 'security-and-sso',
    group: 'organization',
    icon: FiSettings,
    match: orgTab('security'),
    permissions: ['org_unit.manage'],
    modes: ['full'],
    audience: ['org_admin'],
    related: ['two-factor-authentication', 'signing-in'],
    keywords: ['sso', 'security', '2fa enforcement', 'single sign-on'],
  },
  {
    id: 'billing',
    group: 'organization',
    icon: FiCreditCard,
    match: orgTab('billing'),
    permissions: ['org_unit.manage'],
    modes: ['full'],
    audience: ['org_admin'],
    related: ['organization-profile-and-structure'],
    keywords: ['billing', 'subscription', 'checkout', 'portal', 'plan'],
  },
  {
    id: 'mobile-licensing',
    group: 'organization',
    icon: FiSmartphone,
    match: orgTab('licensing'),
    permissions: ['pacs_device.manage'],
    modes: ['full'],
    audience: ['it', ...ADMINS],
    related: ['pacs-connectivity'],
    keywords: [
      'mobile',
      'licenses',
      'seats',
      'app releases',
      'devices',
      'training',
    ],
  },
  {
    id: 'api-as-a-service',
    group: 'developers',
    icon: FiCode,
    routes: ['/api-as-service'],
    audience: ALL_ROLES,
    related: ['playground'],
    keywords: ['api', 'keys', 'usage', 'documentation'],
  },
  {
    id: 'playground',
    group: 'developers',
    icon: FiZap,
    routes: ['/playground'],
    modes: ['full'],
    audience: ALL_ROLES,
    related: ['api-as-a-service', 'understanding-ai-results'],
    keywords: [
      'playground',
      'ai',
      'test images',
      'inference',
      'upload',
      'quick inference',
    ],
  },
  {
    id: 'surveys',
    group: 'other',
    icon: FiClipboard,
    routes: ['/surveys'],
    audience: ALL_ROLES,
    related: ['getting-started'],
    keywords: ['surveys', 'questionnaire', 'feedback', 'pending', 'recurring'],
  },
  {
    id: 'reporting-a-problem',
    group: 'other',
    icon: FiMessageSquare,
    routes: [],
    audience: ALL_ROLES,
    related: ['getting-started', 'audit-trail'],
    keywords: [
      'report a problem',
      'bug',
      'feedback',
      'issue',
      'quality',
      'support',
      'incident',
    ],
  },
];

export function getGuide(id: string): GuideMeta | undefined {
  return GUIDES.find((g) => g.id === id);
}
